import { signal } from "@preact/signals";
import type { HistogramSnapshot, RegistrySnapshot } from "@roguelik/shared-telemetry";
import { useEffect, useRef } from "preact/hooks";
import { isMuted, setMuted } from "../../sound.js";
import { act, camera, devOpen, metrics, net } from "../store.js";

/** Mute state mirrored into a signal so the toggle button re-renders. */
const mutedSig = signal(isMuted());

function hist(s: RegistrySnapshot, name: string): HistogramSnapshot | undefined {
  const m = s[name];
  return m?.kind === "histogram" ? m : undefined;
}
const f1 = (n: number): string => n.toFixed(1);

const SAMPLES = 120;

/** Tiny rolling sparkline (canvas) — preserves the self-test frame/render instrument. */
function Sparkline({ value, color, softMax }: { value: number; color: string; softMax: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const data = useRef<Float32Array>(new Float32Array(SAMPLES));
  const head = useRef(0);
  const filled = useRef(0);
  useEffect(() => {
    data.current[head.current] = value;
    head.current = (head.current + 1) % SAMPLES;
    if (filled.current < SAMPLES) filled.current += 1;
    const ctx = ref.current?.getContext("2d");
    if (!ctx) return;
    const w = 120;
    const h = 24;
    ctx.clearRect(0, 0, w, h);
    let max = softMax;
    for (let i = 0; i < filled.current; i += 1) max = Math.max(max, data.current[i] ?? 0);
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    for (let i = 0; i < filled.current; i += 1) {
      const idx = (head.current - filled.current + i + SAMPLES * 2) % SAMPLES;
      const v = data.current[idx] ?? 0;
      const x = (i / (SAMPLES - 1)) * w;
      const y = h - (v / max) * (h - 2) - 1;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, [value, color, softMax]);
  return <canvas ref={ref} width={120} height={24} class="rounded bg-black/35" />;
}

/** Collapsible dev/self-test panel (toggle with `` ` ``). Metrics + camera + dev A/B toggles. */
export function DevPanel() {
  if (!devOpen.value) return null;
  const m = metrics.value;
  const n = net.value;
  const cam = camera.value;
  const frame = m ? hist(m.snapshot, "client.frame_ms") : undefined;
  const render = m ? hist(m.snapshot, "client.render_ms") : undefined;
  const a = act();

  return (
    <div class="pointer absolute left-2.5 top-2.5 w-72 select-none rounded-md border border-sky-400/25 bg-panel/80 p-2.5 text-[11px] leading-relaxed text-[#cdd9ff] backdrop-blur-sm">
      <div class="mb-1 flex items-center justify-between">
        <span class="font-bold">Dev / self-test</span>
        <button
          type="button"
          class="opacity-60 hover:opacity-100"
          onClick={() => (devOpen.value = false)}
        >
          ✕
        </button>
      </div>
      <div class="tabular-nums">fps {m ? m.fps.toFixed(0) : "—"}</div>
      <div class="flex items-center gap-2">
        <span class="w-20">frame {f1(frame?.p50 ?? 0)}</span>
        <Sparkline value={frame?.p50 ?? 0} color="#7fd1ff" softMax={20} />
      </div>
      <div class="flex items-center gap-2">
        <span class="w-20">rendr {f1(render?.p50 ?? 0)}</span>
        <Sparkline value={render?.p50 ?? 0} color="#9affae" softMax={12} />
      </div>
      <div class="mt-1">
        net {n?.status ?? "—"} · rtt {n?.rttMs.toFixed(0) ?? "—"} · inj {n?.injectedRttMs ?? 0} ·
        room {n?.roomCode || "—"} {n ? `${n.players}p` : ""}
      </div>
      <div>
        pred {n?.predict ? "on" : "off"} · recon {n?.reconError.toFixed(2) ?? "0"}u · rewind{" "}
        {n?.rewind ? "on" : "off"}
      </div>
      <div class="mt-2 flex flex-wrap gap-1.5">
        <Btn onClick={() => a?.cameraToggle()}>cam: {cam?.mode ?? "angled"}</Btn>
        <Btn onClick={() => a?.cycleLatency()}>latency {n?.injectedRttMs ?? 0}ms</Btn>
        <Btn onClick={() => a?.togglePrediction()}>pred {n?.predict ? "on" : "off"}</Btn>
        <Btn onClick={() => a?.toggleRewind()}>rewind {n?.rewind ? "on" : "off"}</Btn>
        <Btn
          onClick={() => {
            const next = !mutedSig.value;
            mutedSig.value = next;
            setMuted(next);
          }}
        >
          sound {mutedSig.value ? "off" : "on"}
        </Btn>
      </div>
      {cam?.mode === "angled" && (
        <div class="mt-1 opacity-70">
          pitch {cam.pitchDeg}° · dist {cam.distance} · fov {cam.fov} (keys [ ] - = ; ')
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick }: { children: preact.ComponentChildren; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      class="rounded border border-sky-400/35 bg-sky-500/15 px-2 py-0.5 hover:bg-sky-500/30"
    >
      {children}
    </button>
  );
}
